import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import {
  Label,
  StyledForm,
  Input,
  SubmitButton,
} from "globalComponents/FormComponents";
import defaultImg from "../../assets/default.png";
import { Button, FolderContainer } from "globalComponents";
import { addNotification } from "store/slices/uiSlice";
import { notifTags } from "globalComponents/Notification";
import { errorHandler } from "globalResources/util";
import { getTheme } from "Theme";

const theme = getTheme();

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 10px 10%;
  @media (max-width: 700px) {
    padding: 10px 16px;
  }
`;
const Photo = styled.img`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid black;
`;
const PhotoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
`;
const Select = styled.select`
  padding: 8px;
  border-radius: 5px;
  border: 2px solid ${theme.INPUT_BORDER};
`;
const ErrorText = styled.p`
  color: ${theme.NEGATIVE};
  margin: 0;
`;

const emptyProfile = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  language: "english",
};

const UserSettings = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const fileInput = useRef(null);
  const [profile, setProfile] = useState(emptyProfile);
  const [hasPassword, setHasPassword] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [defaultAccountId, setDefaultAccountId] = useState("");
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [passwords, setPasswords] = useState({
    current: "",
    next: "",
    confirm: "",
  });
  const [passwordError, setPasswordError] = useState("");

  const notify = (type, message) =>
    dispatch(addNotification({ type, message }));

  const load = useCallback(async () => {
    try {
      const [me, users, photo] = await Promise.all([
        axios.get("/root/user"),
        axios.get("/root/users"),
        axios.get("/root/photo"),
      ]);
      const u = me.data;
      setProfile({
        firstName: u.firstName || "",
        lastName: u.lastName || "",
        email: u.email || "",
        phone: u.phone || "",
        language: u.language || "english",
      });
      setHasPassword(!!u.hasPassword);
      const list = users.data.users || [];
      setCompanies(list);
      const current = u.users?.[u.defaultUser || 0];
      setDefaultAccountId(current?.accountId || list[0]?.account?._id || "");
      setPhotoUrl(photo.data.url);
    } catch (err) {
      const { message } = await errorHandler(err);
      notify(notifTags.error, message || "Couldn't load your settings");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveProfile = async () => {
    setProfileError("");
    if (!profile.firstName.trim() || !profile.lastName.trim())
      return setProfileError("First and last name are required");
    try {
      const res = await axios.put("/root/user", {
        ...profile,
        ...(defaultAccountId && { defaultAccountId }),
      });
      (res.data.warnings || []).forEach((w) => notify(notifTags.warning, w));
      notify(notifTags.success, "Settings saved");
      load();
    } catch (err) {
      const { message } = await errorHandler(err);
      setProfileError(message || "Couldn't save your settings");
    }
  };

  const uploadPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/"))
      return notify(notifTags.error, "Choose an image file");
    setUploading(true);
    try {
      const { data } = await axios.post("/root/photo/presign", {
        fileName: file.name,
        contentType: file.type,
      });
      const form = new FormData();
      Object.entries(data.fields).forEach(([k, v]) => form.append(k, v));
      form.append("file", file);
      // straight to S3, without the app's auth header
      const upload = await fetch(data.url, { method: "POST", body: form });
      if (!upload.ok) throw new Error("Upload failed");
      await axios.put("/root/user", { imageKey: data.key });
      const photo = await axios.get("/root/photo");
      setPhotoUrl(photo.data.url);
      notify(notifTags.success, "Photo updated");
    } catch (err) {
      const { message } = await errorHandler(err);
      notify(notifTags.error, message || "Couldn't upload the photo");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async () => {
    try {
      await axios.put("/root/user", { imageKey: null });
      setPhotoUrl(null);
    } catch (err) {
      const { message } = await errorHandler(err);
      notify(notifTags.error, message);
    }
  };

  const changePassword = async () => {
    setPasswordError("");
    if (passwords.next.length < 8)
      return setPasswordError("New password must be at least 8 characters");
    if (passwords.next !== passwords.confirm)
      return setPasswordError("New passwords don't match");
    try {
      await axios.patch("/root/password/change", {
        currentPassword: passwords.current,
        newPassword: passwords.next,
      });
      setPasswords({ current: "", next: "", confirm: "" });
      setHasPassword(true);
      notify(notifTags.success, "Password changed");
    } catch (err) {
      const { message } = await errorHandler(err);
      setPasswordError(message || "Couldn't change your password");
    }
  };

  const field = (key) => ({
    name: key,
    value: profile[key],
    onChange: (e) => setProfile({ ...profile, [key]: e.target.value }),
  });

  return (
    <Main>
      <FolderContainer title="Profile">
        <StyledForm>
          <PhotoRow>
            <Photo
              src={photoUrl || defaultImg}
              alt="Profile"
              data-testid="profile-photo"
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                name="photo"
                style={{ display: "none" }}
                onChange={uploadPhoto}
              />
              <Button onClick={() => fileInput.current?.click()}>
                {uploading
                  ? "Uploading..."
                  : photoUrl
                    ? "Change Photo"
                    : "Add Photo"}
              </Button>
              {photoUrl && (
                <Button color={theme.BUTTON_COLOR_GREY} onClick={removePhoto}>
                  Remove Photo
                </Button>
              )}
            </div>{" "}
            <Button
              style={{ marginTop: 8, marginLeft: "auto" }}
              onClick={() => history.push("/onboarding/account")}
            >
              Create or Join Another Company
            </Button>
          </PhotoRow>
          <Label>First Name</Label>
          <Input type="text" {...field("firstName")} />
          <Label>Last Name</Label>
          <Input type="text" {...field("lastName")} />
          <Label>Email</Label>
          <Input type="email" {...field("email")} />
          <Label>Phone</Label>
          <Input type="tel" {...field("phone")} />
          <Label>Language</Label>
          <Select {...field("language")}>
            <option value="english">English</option>
            <option value="spanish">Español</option>
          </Select>
          <Label tooltip="The company you're logged into when you sign in">
            Default Company
          </Label>
          <Select
            name="defaultAccountId"
            value={defaultAccountId}
            onChange={(e) => setDefaultAccountId(e.target.value)}
          >
            {companies.map((c) => (
              <option key={c.account._id} value={c.account._id}>
                {c.account.name} (
                {c.user.accountType === "super" ? "Owner" : c.user.accountType})
              </option>
            ))}
          </Select>
          {profileError && <ErrorText>{profileError}</ErrorText>}
          <SubmitButton onClick={saveProfile}>Save Changes</SubmitButton>
        </StyledForm>
      </FolderContainer>
      <FolderContainer title="Password">
        <StyledForm>
          {hasPassword && (
            <>
              <Label>Current Password</Label>
              <Input
                type="password"
                name="currentPassword"
                value={passwords.current}
                onChange={(e) =>
                  setPasswords({ ...passwords, current: e.target.value })
                }
              />
            </>
          )}
          <Label>New Password</Label>
          <Input
            type="password"
            name="newPassword"
            value={passwords.next}
            onChange={(e) =>
              setPasswords({ ...passwords, next: e.target.value })
            }
          />
          <Label>Confirm New Password</Label>
          <Input
            type="password"
            name="confirmPassword"
            value={passwords.confirm}
            onChange={(e) =>
              setPasswords({ ...passwords, confirm: e.target.value })
            }
          />
          {passwordError && <ErrorText>{passwordError}</ErrorText>}
          <SubmitButton onClick={changePassword}>
            {hasPassword ? "Change Password" : "Set Password"}
          </SubmitButton>
        </StyledForm>
      </FolderContainer>
    </Main>
  );
};

export default UserSettings;
